import { useState } from "react"
import { ArrowLeft, ArrowRight, Loader2, Shield } from "lucide-react"
import { useWizard } from "./WizardContext"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { calculateEstimate } from "@/lib/pricing"
import { submitLead } from "@/lib/lead-manager"

export default function ContactStep() {
  const { state, setContact, setEstimate, setLoading, setError, goToStep, prevStep } = useWizard()
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const validate = (): boolean => {
    const errors: Record<string, string> = {}
    if (!state.contact.firstName.trim()) errors.firstName = "First name is required"
    if (!state.contact.email.trim()) errors.email = "Email is required"
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.contact.email)) errors.email = "Enter a valid email"
    if (!state.contact.phone.trim()) errors.phone = "Phone number is required"
    if (!state.contact.consent) errors.consent = "Please agree to continue"
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setLoading(true)

    try {
      // Calculate estimate
      const estimate = calculateEstimate({
        roofSizeSqft: state.roofSizeSqft || 2000,
        complexity: state.complexity || "normal",
        selectedMaterial: state.selectedMaterial,
        region: "midwest",
        hasSkylights: state.features.hasSkylights,
        skylightCount: state.features.skylightCount,
        hasMultipleStories: state.features.hasMultipleStories,
        hasChimney: state.features.hasChimney,
        hasAsbestos: state.features.hasAsbestos,
        detachedGarageSize: state.detachedGarageSize,
      })

      // Submit lead
      submitLead({
        name: `${state.contact.firstName} ${state.contact.lastName}`.trim(),
        email: state.contact.email,
        phone: state.contact.phone,
        service: "Roof Replacement Estimate",
        message: JSON.stringify({
          address: state.formattedAddress,
          roofSize: state.roofSizeSqft,
          material: state.selectedMaterial,
          complexity: state.complexity,
          totalCost: estimate.totalCost,
          timeline: state.timeline,
          paymentMethod: state.paymentMethod,
        }),
      })

      setEstimate(estimate)
      goToStep(8)
    } catch {
      setError("Something went wrong. Please try again.")
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground tracking-tight">
          Almost there!
        </h2>
        <p className="text-muted-foreground mt-1">
          Enter your contact info and we'll generate your personalized estimate.
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="firstName" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              First Name *
            </Label>
            <Input
              id="firstName"
              name="firstName"
              autoComplete="given-name"
              value={state.contact.firstName}
              onChange={(e) => setContact({ firstName: e.target.value })}
              className={`h-12 rounded-xl text-[15px] ${validationErrors.firstName ? "border-destructive" : ""}`}
              placeholder="John"
            />
            {validationErrors.firstName && <p className="text-xs text-destructive">{validationErrors.firstName}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Last Name
            </Label>
            <Input
              id="lastName"
              name="lastName"
              autoComplete="family-name"
              value={state.contact.lastName}
              onChange={(e) => setContact({ lastName: e.target.value })}
              className="h-12 rounded-xl text-[15px]"
              placeholder="Smith"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Email *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={state.contact.email}
            onChange={(e) => setContact({ email: e.target.value })}
            className={`h-12 rounded-xl text-[15px] ${validationErrors.email ? "border-destructive" : ""}`}
            placeholder="john@example.com"
          />
          {validationErrors.email && <p className="text-xs text-destructive">{validationErrors.email}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Phone *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            value={state.contact.phone}
            onChange={(e) => setContact({ phone: e.target.value })}
            className={`h-12 rounded-xl text-[15px] ${validationErrors.phone ? "border-destructive" : ""}`}
            placeholder="(555) 123-4567"
          />
          {validationErrors.phone && <p className="text-xs text-destructive">{validationErrors.phone}</p>}
        </div>

        <div className="flex items-start gap-3 pt-2">
          <Checkbox
            id="consent"
            checked={state.contact.consent}
            onCheckedChange={(checked) => setContact({ consent: checked === true })}
            className="mt-0.5"
          />
          <label htmlFor="consent" className="text-sm text-muted-foreground leading-snug cursor-pointer">
            I agree to receive my estimate and follow-up communications from Skyline Roofing.
            {validationErrors.consent && <span className="text-destructive block text-xs mt-1">{validationErrors.consent}</span>}
          </label>
        </div>
      </div>

      {state.error && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">{state.error}</div>
      )}

      {/* Navigation */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={prevStep} className="h-12 px-6 rounded-xl font-semibold">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={state.loading}
          className="flex-1 h-13 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold text-base transition-all hover:shadow-lg hover:shadow-primary/25 disabled:opacity-60"
        >
          {state.loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              Get My Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>
      </div>

      {/* Trust */}
      <div className="flex items-center gap-2 text-muted-foreground text-xs">
        <Shield className="w-3.5 h-3.5" />
        <span>No spam. No obligation. Your info stays private.</span>
      </div>
    </div>
  )
}
